import { DownOutlined, InfoCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Popover, Tooltip } from 'antd';
import { formatNumber } from 'utils/numberFormat';
import { HTMLAttributes, memo, ReactNode, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import Fold from './Fold';
import { Between } from './TradeRefund';
import { cx } from '@emotion/css';

export const DetailItem = styled(Between)`
  height: 32px;
  font-size: 12px;
  color: #515359;
`;

export const Detail = styled.ul`
  margin: 0;
  padding: 8px 18px;
  list-style: none;
  background-color: #f7f9fa;

  li {
    display: flex;
    align-items: center;
    line-height: 26px;
    font-size: 14px;
    > dt {
      flex: 1 0 0;
      text-align: right;
      font-weight: 400;
      color: #515359;
    }
    > dd {
      flex: 0 0 130px;
      margin-bottom: 0;
      text-align: right;
      color: #232426;
    }
    > dd.colorFont {
      color: var(--fett-colors-price);
      font-weight: 500;
    }
  }
`;

const TransportExpense = styled.dd`
  margin-right: -14px;
  margin-left: 14px;
  > span {
    margin-left: 2px;
    cursor: pointer;
  }
`;
const QuestionIcon = styled(QuestionCircleOutlined)`
  margin-left: 4px;
`;

export const Amount = styled.div<{ isfold?: boolean }>`
  display: flex;
  align-items: center;
  > div {
    color: var(--fett-colors-price);
  }
  > span {
    margin-left: 8px;
    font-size: 10px;
    cursor: pointer;
    transform: ${({ isfold = true }) => (isfold ? 'rotate(0)' : 'rotate(180deg)')};
    transition: all 0.2s;
  }
`;

export const priceRender = <T extends string | number>(
  price: T | false,
  renderer: (price: T) => ReactNode,
) => {
  return price || price === 0 ? renderer(price) : null;
};

export interface TradePaymentAmountProps extends HTMLAttributes<HTMLElement> {
  trade: Conversation.Trade.Vo;
  defaultFold?: boolean;
  storeType?: IM_API.Admin.StoreType;
  exportMode?: boolean;
}

/** LAZADA 买家实付金额 */
function TradePaymentAmountLazada(props: TradePaymentAmountProps) {
  const {
    trade,
    exportMode = false,
    storeType,
    defaultFold = true,
    className,
    ...otherProps
  } = props;

  const {
    unit,
    totalAmount,
    itemPrice,
    shippingFeeOriginal,
    shippingFeeDiscount,
    shippingFeeActual,
    sellerPromotion,
    voucherPlatform,
    voucherSeller,
    adjustAmount,
  } = trade;

  const { t } = useTranslate();

  const [isfold, setIsfold] = useState(defaultFold);

  const total = priceRender(totalAmount, (totalAmount) => {
    return (
      <DetailItem
        key="total"
        onClick={() => setIsfold((isfold) => !isfold)}
        style={
          className === 'tradeDetail'
            ? { border: '1px solid #F0F2F5', padding: '0 16px', lineHeight: '40px' }
            : {}
        }
      >
        <span style={className === 'tradeDetail' ? { fontWeight: 'bold' } : {}}>
          {t('trade.paymentAmount')}
          {trade.region === 'ID' && storeType === 'CROSS' ? (
            <Tooltip title={t('trade.未包含买家支付的进口关税')}>
              <QuestionIcon />
            </Tooltip>
          ) : null}
        </span>
        {className !== 'tradeDetail' ? (
          <Amount isfold={isfold}>
            <div>{`${unit} ${formatNumber(totalAmount, '0,0.00')}`}</div>
            <DownOutlined />
          </Amount>
        ) : null}
      </DetailItem>
    );
  });

  const detail = (
    <Detail
      key="detail"
      style={
        className === 'tradeDetail'
          ? {
              background: 'none',
              border: '1px solid #F0F2F5',
              borderTop: 'none',
            }
          : {}
      }
    >
      {priceRender(itemPrice, (itemPrice) => {
        return (
          <li>
            <dt>{`${t('trade.商品小计')}:`}</dt>
            <dd>{`${unit} ${formatNumber(itemPrice, '0,0.00')}`}</dd>
          </li>
        );
      })}
      {priceRender(shippingFeeActual, (shippingFeeActual) => {
        return (
          <li>
            <dt>{`${t('trade.运输费用')}:`}</dt>
            <TransportExpense
              className={cx({
                'pr-[14px]': exportMode,
              })}
            >
              {`${unit} ${formatNumber(shippingFeeActual, '0,0.00')}`}
              {!exportMode && (
                <Popover
                  placement="topRight"
                  content={
                    <div>
                      {priceRender(shippingFeeOriginal, (shippingFeeOriginal) => {
                        return (
                          <div>
                            <span>{`${t('trade.原始费用')}: `}</span>
                            <span>{`${unit} ${formatNumber(shippingFeeOriginal, '0,0.00')}`}</span>
                          </div>
                        );
                      })}
                      {priceRender(shippingFeeDiscount, (shippingFeeDiscount) => {
                        return (
                          <div>
                            <span>{`${t('trade.运费折扣')}: `}</span>
                            <span>{`${unit} ${formatNumber(shippingFeeDiscount, '0,0.00')}`}</span>
                          </div>
                        );
                      })}
                    </div>
                  }
                >
                  <InfoCircleOutlined />
                </Popover>
              )}
            </TransportExpense>
          </li>
        );
      })}
      {/* 购物券暂时无法拿到 */}
      {/* <li>
        <dt>{`${t('trade.购物券')}:`}</dt>
        <dd>{`${unit} ${formatNumber(payment, '0,0.00')}`}</dd>
      </li> */}
      {priceRender(sellerPromotion, (sellerPromotion) => {
        return (
          <li>
            <dt>{`${t('trade.商家优惠券')}:`}</dt>
            <dd>{`${unit} ${formatNumber(sellerPromotion, '0,0.00')}`}</dd>
          </li>
        );
      })}
      {priceRender(totalAmount, (totalAmount) => {
        return (
          <li>
            <dt>{`${t('trade.买家付款总额')}:`}</dt>
            <dd>{`${unit} ${formatNumber(totalAmount, '0,0.00')}`}</dd>
          </li>
        );
      })}
      {priceRender(voucherPlatform, (voucherPlatform) => {
        return (
          <li>
            <dt>{`${t('trade.Lazada总折扣')}:`}</dt>
            <dd>{`${unit} ${formatNumber(voucherPlatform, '0,0.00')}`}</dd>
          </li>
        );
      })}
      {priceRender(voucherSeller, (voucherSeller) => {
        return (
          <li>
            <dt>{`${t('trade.卖家折扣总额')}:`}</dt>
            <dd>{`${unit} ${formatNumber(voucherSeller, '0,0.00')}`}</dd>
          </li>
        );
      })}
      {priceRender(adjustAmount, (adjustAmount) => {
        return (
          <li>
            <dt>{`${t('trade.调整金额')}:`}</dt>
            <dd>{`${unit} ${formatNumber(adjustAmount, '0,0.00')}`}</dd>
          </li>
        );
      })}
    </Detail>
  );

  return total ? (
    <Fold
      isfold={isfold}
      fixedCount={1}
      nodeList={[total, detail]}
      defaultFixedHeight={32}
      style={className === 'tradeDetail' ? { marginBottom: '16px' } : {}}
      {...otherProps}
    />
  ) : null;
}

export default memo(TradePaymentAmountLazada);
